import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, ArrowRightLeft, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button, DataTable, FormField, Modal, PageHeader, StatusBadge } from '@/components/ui';
import { AnalyseIAModal } from './AnalyseIAModal';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type AppelOffreDTO,
  type AppelOffreInput,
  type AOStatut,
  AO_STATUTS,
  convertirEnMarche,
  createAppelOffre,
  deleteAppelOffre,
  listAppelsOffres,
  updateAppelOffre,
} from '@/services/marches';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const AO_STATUT_UI: Record<AOStatut, { label: string; status: StatusKey }> = {
  IDENTIFIE: { label: 'Identifié', status: 'pending' },
  EN_COURS: { label: 'En cours', status: 'working' },
  DEPOSE: { label: 'Déposé', status: 'info' },
  REMPORTE: { label: 'Remporté', status: 'done' },
  PERDU: { label: 'Perdu', status: 'stuck' },
  ANNULE: { label: 'Annulé', status: 'pending' },
};

const toForm = (a: AppelOffreDTO): AppelOffreInput => ({
  reference: a.reference, intitule: a.intitule, type: a.type, montant_estimatif: a.montant_estimatif,
  caution_soumission: a.caution_soumission, date_limite_depot: a.date_limite_depot, statut: a.statut,
});

export function AppelsOffresPage() {
  const perms = usePerms('appels_offres');
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [iaOpen, setIaOpen] = useState(false);
  const [editing, setEditing] = useState<AppelOffreDTO | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['appels-offres', { showArchived }], queryFn: () => listAppelsOffres({ archives: showArchived }) });
  const { register, handleSubmit, reset } = useForm<AppelOffreInput>({
    defaultValues: { type: 'PUBLIC', statut: 'IDENTIFIE' },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['appels-offres'] });
  const openCreate = () => { setEditing(null); reset({ type: 'PUBLIC', statut: 'IDENTIFIE', reference: '', intitule: '' }); setModalOpen(true); };
  const openEdit = (a: AppelOffreDTO) => { setEditing(a); reset(toForm(a)); setModalOpen(true); };

  const createMutation = useMutation({
    mutationFn: (v: AppelOffreInput) => {
      const payload = {
        ...v,
        montant_estimatif: v.montant_estimatif ? Number(v.montant_estimatif) : null,
        caution_soumission: v.caution_soumission ? Number(v.caution_soumission) : null,
      };
      return editing ? updateAppelOffre(editing.id, payload) : createAppelOffre(payload);
    },
    onSuccess: () => {
      invalidate();
      setModalOpen(false);
      reset();
      toast.success(editing ? "Appel d'offres modifié." : "Appel d'offres créé.");
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateAppelOffre(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Archivé.' : 'Restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAppelOffre(id),
    onSuccess: () => { invalidate(); toast.success('Supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const convertMutation = useMutation({
    mutationFn: convertirEnMarche,
    onSuccess: (m) => {
      invalidate();
      toast.success(`Converti en marché ${m.reference_marche}.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<AppelOffreDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 130 },
    { accessorKey: 'intitule', header: 'Intitulé' },
    { accessorKey: 'type', header: 'Type', size: 100, cell: ({ getValue }) => <span className="text-xs">{String(getValue()).replace('_', ' ')}</span> },
    {
      accessorKey: 'montant_estimatif',
      header: 'Montant estimatif',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? <span className="tabular-nums">{formatDZD(v, { decimals: 0 })}</span> : <span className="text-content-muted">—</span>;
      },
    },
    { accessorKey: 'date_limite_depot', header: 'Date limite', size: 120, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    {
      accessorKey: 'statut',
      header: 'Statut',
      size: 130,
      cell: ({ getValue }) => {
        const ui = AO_STATUT_UI[getValue() as AOStatut];
        return <StatusBadge status={ui.status} label={ui.label} />;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 200,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          {row.original.statut === 'REMPORTE' && !showArchived && (
            <Button variant="ghost" size="sm" onClick={() => convertMutation.mutate(row.original.id)} disabled={convertMutation.isPending}>
              <ArrowRightLeft className="size-4" /> Marché
            </Button>
          )}
          {showArchived ? (
            perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: false })} aria-label="Restaurer"><ArchiveRestore className="size-4" /></Button>
          ) : (
            <>
              {perms.canModifier && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} aria-label="Modifier"><Pencil className="size-4" /></Button>}
              {perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: true })} aria-label="Archiver"><Archive className="size-4" /></Button>}
            </>
          )}
          {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer « ${row.original.reference} » ?`)) deleteMutation.mutate(row.original.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Appels d'offres & marchés"
        subtitle="Veille, soumission et conversion en marchés"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: "Appels d'offres" }]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Archivés
            </label>
            <Button variant="secondary" onClick={() => setIaOpen(true)}>
              <Sparkles /> Analyser un PDF avec l'IA
            </Button>
            {perms.canAjouter && (
              <Button onClick={openCreate}>
                <Plus /> Nouvel appel d'offres
              </Button>
            )}
          </div>
        }
      />

      <AnalyseIAModal open={iaOpen} onOpenChange={setIaOpen} />

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pageSize={15}
        exportFileName="appels-offres"
        emptyMessage="Aucun appel d'offres enregistré."
      />

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing ? "Modifier l'appel d'offres" : "Nouvel appel d'offres"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Référence">
            <Input placeholder="Auto (AO-001…)" disabled={!!editing} {...register('reference')} />
          </FormField>
          <FormField label="Type">
            <select {...register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="PUBLIC">Public</option>
              <option value="PRIVE">Privé</option>
              <option value="GRE_A_GRE">Gré à gré</option>
            </select>
          </FormField>
          <FormField label="Intitulé" required className="sm:col-span-2">
            <Input {...register('intitule')} />
          </FormField>
          <FormField label="Montant estimatif (DZD)">
            <Input type="number" step="1" {...register('montant_estimatif')} />
          </FormField>
          <FormField label="Caution soumission (DZD)">
            <Input type="number" step="1" {...register('caution_soumission')} />
          </FormField>
          <FormField label="Date limite de dépôt">
            <Input type="date" {...register('date_limite_depot')} />
          </FormField>
          <FormField label="Statut">
            <select {...register('statut')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              {AO_STATUTS.map((s) => (
                <option key={s} value={s}>
                  {AO_STATUT_UI[s].label}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </Modal>
    </>
  );
}
